import { useState, useRef } from "react";
import axios from "axios";
import { Reveal, RevealText } from "./Reveal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "", company: "", website: "" });
  const mountedAt = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Completa los campos obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        ...form,
        elapsed_ms: Date.now() - mountedAt.current,
      });
      if (res.data?.status === "success") {
        setSubmitted(true);
        toast.success("Transmisión recibida. Te contactaremos en breve.");
        setForm({ name: "", email: "", message: "", company: "", website: "" });
      } else {
        toast.error("No pudimos completar la transmisión.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error de red. Inténtalo de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error al enviar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-20 md:py-28 bg-[var(--bb-bg)] border-t border-[var(--bb-border)]"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <div className="mono-label mb-8">[ 05 / Establecer Conexión ]</div>
          </Reveal>
          <h2 className="font-display text-[12vw] md:text-[6.5vw] leading-[0.95] tracking-[-0.03em] text-[var(--bb-text)] mb-10">
            <RevealText text="Establecer" />
            <br />
            <span className="font-serif-italic text-[var(--bb-copper-light)]">
              <RevealText text="Conexión." delay={0.25} />
            </span>
          </h2>
          <Reveal delay={0.3}>
            <p className="font-body text-[var(--bb-text-2)] text-lg leading-relaxed max-w-md mb-12">
              Inicie una consulta para arquitectura personalizada. Cada
              despliegue comienza con un diálogo directo con nuestros
              arquitectos senior.
            </p>
          </Reveal>

          <Reveal delay={0.45}>
            <div className="flex flex-col gap-6 font-mono text-[12px] tracking-[0.18em] uppercase">
              <div>
                <div className="mono-label mb-2">Oficina</div>
                <div className="text-[var(--bb-text)] text-sm tracking-[0.1em]">
                  Madrid, España
                </div>
              </div>
              <div>
                <div className="mono-label mb-2">Respuesta</div>
                <div className="text-[var(--bb-text)] text-sm tracking-[0.1em]">
                  &lt; 24 horas · Arquitecto senior
                </div>
              </div>
              <div>
                <div className="mono-label mb-2">NDA</div>
                <div className="text-[var(--bb-text)] text-sm tracking-[0.1em]">
                  Disponible bajo petición
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 md:col-start-7 relative">
          <Reveal delay={0.2}>
            <form
              onSubmit={onSubmit}
              className="relative flex flex-col gap-8 bg-[var(--bb-bg-2)] border border-[var(--bb-border)] p-8 md:p-12"
              data-testid="contact-form"
            >
              <div className="flex items-center justify-between">
                <div className="mono-label">Transmisión · #BBAI-{Math.floor(Math.random() * 9000 + 1000)}</div>
                <div className="flex items-center gap-2 mono-label">
                  <span className="w-2 h-2 bg-[var(--bb-copper)] rounded-full animate-pulse" />
                  <span>Canal Abierto</span>
                </div>
              </div>

              {/* Honeypot: hidden from humans, bots tend to fill it */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
              >
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bb-field">
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder=" "
                    required
                    data-testid="contact-input-name"
                  />
                  <label>Identificación</label>
                </div>
                <div className="bb-field">
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    placeholder=" "
                    data-testid="contact-input-company"
                  />
                  <label>Entidad (opcional)</label>
                </div>
              </div>

              <div className="bb-field">
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder=" "
                  required
                  data-testid="contact-input-email"
                />
                <label>Vector de Transmisión (Email)</label>
              </div>

              <div className="bb-field">
                <textarea
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  placeholder=" "
                  required
                  data-testid="contact-input-message"
                />
                <label>Parámetros</label>
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-4 border-t border-[var(--bb-border)]">
                <p className="mono-label text-[10px] max-w-xs">
                  Al transmitir, aceptas nuestra política de privacidad. Los
                  datos se procesan bajo cifrado end-to-end.
                </p>
                <button
                  type="submit"
                  className="bb-cta"
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  <span>{submitting ? "Transmitiendo…" : submitted ? "Enviado ✓" : "Transmitir"}</span>
                  <span aria-hidden>↗</span>
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
